import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  SFDatagrid,
  SFActionBar,
  SFPageContainer,
  ACTION_BAR_ACTION_TYPES,
  ACTION_BAR_ACTION_NAMES,
} from "@sf-core";
import { PATHS } from "../../constants/paths";
import { GridNames } from "../../constants/gridConfig";
import { useSelector } from "react-redux";
import getNavDefs from "../navDefs/getBenchmarkNavDefs";
import { API_PARAM } from "../../constants/apiParams";
import {
  exportBenchmarkMicroSkillsGrid,
  uploadBenchmarkJMicroSkills,
} from "../../services/benchmarkMicroSkillsService";
import { Claims } from "../../constants/claims";

const BenchmarkMicroSkills = () => {
  const handleMicroSkillsDownload = () => {
    exportBenchmarkMicroSkillsGrid(microSkillsGridLayout?.filterModel ?? {});
  };

  const microSkillsGridLayout = useSelector((state) =>
    state.gridLayout.find(
      (grid) => grid.gridName === GridNames.BENCHMARK_MICROSKILLS
    )
  );

  const { t } = useTranslation();
  const navigate = useNavigate();
  const navDefs = getNavDefs({ t });

  const columnDefs = useMemo(
    () => [
      {
        headerName: t("benchmark.microSkillPathsGrid.rowNumber"),
        field: "rowNumber",
        filter: false,
        hide: false,
      },
      {
        headerName: t("benchmark.microSkillPathsGrid.microSkillCode"),
        field: "microSkillCode",
        filter: true,
        filterParams: {
          order: 1,
        },
        hide: false,
      },
      {
        headerName: t("benchmark.microSkillPathsGrid.microSkillName"),
        field: "microSkillName",
        filter: true,
        filterParams: {
          order: 2,
        },
        hide: false,
      },
      {
        headerName: t("benchmark.microSkillPathsGrid.skillCode"),
        field: "skillCode",
        filter: true,
        filterParams: {
          order: 3,
        },
        hide: false,
      },
      {
        headerName: t("benchmark.microSkillPathsGrid.skillName"),
        field: "skillName",
        filter: true,
        filterParams: {
          order: 4,
        },
        hide: false,
      },
    ],
    []
  );

  const actionDefs = [
    {
      name: ACTION_BAR_ACTION_NAMES.ADD,
      type: ACTION_BAR_ACTION_TYPES.BUTTON,
      permissions: [Claims.AdminBenchmarkMicroSkillsImport],
      onClick: () => navigate(PATHS.BENCHMARK_MICROSKILLS_PATH_ADD),
      buttonProps: {
        label: t("benchmark.addButton.addBenchmarkMicroSkills"),
        variant: "primary",
      },
    },
    {
      name: ACTION_BAR_ACTION_NAMES.DOWNLOAD,
      type: ACTION_BAR_ACTION_TYPES.ICON,
      permissions: [Claims.AdminBenchmarkMicroSkillsExport],
      onClick: handleMicroSkillsDownload,
      iconProps: {
        tooltip: t("benchmark.download.downloadMicroSkills"),
        overflowLabel: t("benchmark.download.downloadMicroSkills"),
      },
    },
    {
      name: "validateMicroSkills",
      type: ACTION_BAR_ACTION_TYPES.BUTTON,
      permissions: [Claims.AdminBenchmarkMicroSkillsUpload],
      onClick: uploadBenchmarkJMicroSkills,
      buttonProps: {
        label: t("benchmark.validate.skillsMicroSkills"),
        variant: "primary",
        async: true,
      },
      confirmation: true,
      confirmationProps: {
        header: t("benchmark.validateButton.validateHeader"),
        message: t("benchmark.validateButton.validateMessage"),
        primaryLabel: t("benchmark.validateButton.validateConfirm"),
        secondaryLabel: t("benchmark.validateButton.validateCancel"),
      },
    },
    {
      name: ACTION_BAR_ACTION_NAMES.FILTER,
      type: ACTION_BAR_ACTION_TYPES.ICON,
      filterParams: {
        gridName: GridNames.BENCHMARK_MICROSKILLS,
      },
    },
  ];

  return (
    <>
      <SFPageContainer disableGutters>
        <SFActionBar
          titleProps={{
            title: t("benchmark.titles.benchmarkMicroSkills"),
          }}
          actionDefs={actionDefs}
          navDefs={navDefs}
        />
        <SFDatagrid
          fitScreen
          canDownload={false}
          columnDefs={columnDefs}
          hasRowSelection={true}
          gridName={GridNames.BENCHMARK_MICROSKILLS}
          datasourceUrl={API_PARAM.BENCHMARK_MICROSKILLS_GRID}
          filterValuesUrl={API_PARAM.BENCHMARK_MICROSKILLS_COL_FILTER}
          basicFiltering
        />
      </SFPageContainer>
    </>
  );
};

export default BenchmarkMicroSkills;
